import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Snackbar, Button, Box } from '@mui/material';
import { useColors } from 'frontend/hooks/useColors';

const FormErrorHandler = ({
  bookingData,
  isSubmitAttempted,
  onErrorProcessed,
  onNavigateToSection,
  passengerFormRefs,
  contactFormRef,
  isInternational,
}) => {
  const { background } = useColors();
  const [open, setOpen] = useState(false);
  const [currentError, setCurrentError] = useState(null);
  const [errorIndex, setErrorIndex] = useState(0);

  const getContactErrors = useCallback(() => {
    if (!contactFormRef?.current) return null;

    const formik = contactFormRef.current;
    const values = formik.getValues() || {};
    const errors = formik.getErrors() || {};
    const touched = formik.touched || {};

    const missingFields = [];

    // Validar campos básicos
    ['nombre', 'apellidos', 'email'].forEach((field) => {
      const isFieldTouched = touched[field] || isSubmitAttempted;
      const hasValue = values[field];
      const hasError = errors[field];

      if (isFieldTouched && (!hasValue || hasError)) {
        missingFields.push(field);
      }
    });

    // Validar teléfonos
    ['telefono1'].forEach((phoneField) => {
      const phoneValue = values[phoneField];
      const phoneError = errors[phoneField];
      const isPhoneTouched = touched[phoneField] || isSubmitAttempted;

      if (isPhoneTouched) {
        if (!phoneValue || !phoneValue.codigo || !phoneValue.numero) {
          missingFields.push(phoneField);
        } else if (phoneError?.codigo || phoneError?.numero) {
          missingFields.push(phoneField);
        }
      }
    });

    // Validar teléfono 2 solo si tiene algún valor
    const telefono2Value = values['telefono2'];
    const telefono2Error = errors['telefono2'];
    const isTelefono2Touched = touched['telefono2'] || isSubmitAttempted;

    if (
      isTelefono2Touched &&
      telefono2Value &&
      (!telefono2Value.codigo ||
        !telefono2Value.numero ||
        telefono2Error?.codigo ||
        telefono2Error?.numero)
    ) {
      missingFields.push('telefono2');
    }

    if (missingFields.length === 0) return null;

    const fieldLabels = {
      nombre: 'Nombre',
      apellidos: 'Apellidos',
      email: 'Email',
      telefono1: 'Teléfono 1',
      telefono2: 'Teléfono 2',
    };

    const missingLabels = missingFields
      .map((field) => {
        if (field.startsWith('telefono')) {
          const phoneValue = values[field];
          const phoneError = errors[field];
          const parts = [];

          if (!phoneValue?.codigo || phoneError?.codigo) {
            parts.push('código de país');
          }
          if (!phoneValue?.numero || phoneError?.numero) {
            parts.push('número');
          }

          return parts.length > 0
            ? `${fieldLabels[field]} (${parts.join(' y ')})`
            : fieldLabels[field];
        }
        return fieldLabels[field];
      })
      .filter(Boolean);

    return {
      type: 'contact',
      message: `Faltan los siguientes campos en el formulario de contacto: ${missingLabels.join(
        ', ',
      )}`,
    };
  }, [contactFormRef, isSubmitAttempted]);

  const getPassengerErrors = useCallback(() => {
    if (!passengerFormRefs?.current) return null;

    const allErrors = [];
    for (let i = 0; i < passengerFormRefs.current.length; i++) {
      const ref = passengerFormRefs.current[i];
      if (ref) {
        const values = ref.getValues();
        const errors = ref.getErrors();
        const fieldLabels = ref.getFieldLabels();
        const missingFields = [];

        // Validar campos básicos
        ['gender', 'name', 'lastName', 'birthDate', 'documentType', 'documentNumber'].forEach(
          (field) => {
            if ((!values[field] || errors[field]) && isSubmitAttempted) {
              missingFields.push(field);
            }
          },
        );

        // Validar edad
        if (values.birthDate && errors.birthDate && isSubmitAttempted) {
          missingFields.push('birthDate');
        }

        if (missingFields.length > 0) {
          allErrors.push({
            type: 'passenger',
            message: `Pasajero ${i + 1}: ${missingFields
              .map((field) => fieldLabels?.[field] || field)
              .join(', ')}`,
            index: i,
          });
        }
      }
    }
    return allErrors.length > 0 ? allErrors[0] : null;
  }, [passengerFormRefs, isSubmitAttempted]);

  const getFiscalDataErrors = useCallback(() => {
    if (!bookingData?.fiscalData || !isInternational) return null;

    const { useCommonData, commonData, data } = bookingData.fiscalData;

    // Si no hay datos fiscales en absoluto, retornar error
    if (!data || data.length === 0) {
      return {
        type: 'fiscal',
        message: 'No hay datos fiscales registrados',
      };
    }

    // Solo validar datos comunes si realmente se están usando
    if (useCommonData && commonData) {
      // Verificar que los datos comunes estén completos
      if (!commonData.documentType || !commonData.cuitCuil) {
        return {
          type: 'fiscal',
          message: 'Los datos fiscales comunes están incompletos',
        };
      }
      return null;
    }

    // Si no se están usando datos comunes, verificar que todos los pasajeros tengan sus datos
    const incompleteFiscalData = data.findIndex((d) => !d || !d.documentType || !d.cuitCuil);
    if (incompleteFiscalData >= 0) {
      return {
        type: 'fiscal',
        message: `El pasajero ${incompleteFiscalData + 1} tiene datos fiscales incompletos`,
      };
    }
    return null;
  }, [bookingData, isInternational]);

  const handleNavigateToError = useCallback(() => {
    if (currentError) {
      switch (currentError.type) {
        case 'contact':
          onNavigateToSection('contact');
          break;
        case 'passenger':
          onNavigateToSection('passenger', currentError.index);
          break;
        case 'fiscal':
          onNavigateToSection('fiscal');
          break;
        default:
          break;
      }
      handleClose();
    }
  }, [currentError, onNavigateToSection]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (open && isSubmitAttempted) {
      const errors = [getContactErrors(), getPassengerErrors(), getFiscalDataErrors()].filter(
        (error) => error !== null,
      );

      if (errors.length > 0) {
        setCurrentError(errors[errorIndex]);
      } else {
        handleClose();
        setCurrentError(null);
        setErrorIndex(0);
        onErrorProcessed?.();
      }
    }
  }, [
    open,
    isSubmitAttempted,
    getContactErrors,
    getPassengerErrors,
    getFiscalDataErrors,
    errorIndex,
    handleClose,
    onErrorProcessed,
  ]);

  useEffect(() => {
    if (isSubmitAttempted) {
      setOpen(true);
      const timer = setTimeout(() => {
        onErrorProcessed?.();
      }, 10000);

      return () => clearTimeout(timer);
    } else {
      setOpen(false);
      setCurrentError(null);
      setErrorIndex(0);
    }
  }, [isSubmitAttempted, onErrorProcessed]);

  if (!open || !isSubmitAttempted || !currentError?.message) return null;

  return (
    <Snackbar
      open={open}
      autoHideDuration={10000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleNavigateToError}
              sx={{ color: background }}
            >
              Ir al error
            </Button>
          }
        >
          {currentError.message}
        </Alert>
      </Box>
    </Snackbar>
  );
};

export default FormErrorHandler;
